import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Download, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '@/lib/utils';

// Firebase
import { db, auth } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';

const numberToWords = (num, currencyCode = 'INR') => {
  const currencyMap = {
    'INR': 'Rupees',
    'USD': 'Dollars',
    'EUR': 'Euros',
    'GBP': 'Pounds',
    'AUD': 'Dollars',
  };
  const currencyStr = currencyMap[currencyCode] || currencyCode;

  if (num === 0) return `Zero ${currencyStr} only`;

  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const formatWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + formatWords(n % 100) : '');

    if (n < 100000) {
      return formatWords(Math.floor(n / 1000)) + ' Thousand ' + formatWords(n % 1000);
    }
    if (n < 10000000) {
      return formatWords(Math.floor(n / 100000)) + ' Lakh ' + formatWords(n % 100000);
    }
    return formatWords(Math.floor(n / 10000000)) + ' Crore ' + formatWords(n % 10000000);
  };

  const integerPart = Math.floor(num);
  const words = formatWords(integerPart).trim().replace(/\s+/g, ' ');
  return `${words} ${currencyStr} only`;
};

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [business, setBusiness] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringData, setRecurringData] = useState({
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });

  // only for hiding buttons during PDF export
  const [isExporting, setIsExporting] = useState(false);
  const invoiceRef = useRef(null);

  useEffect(() => {
    fetchInvoiceData();
  }, [invoiceId]);

  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const invoiceDocRef = doc(db, 'invoices', invoiceId);
      const invoiceSnap = await getDoc(invoiceDocRef);

      if (!invoiceSnap.exists()) {
        setInvoice(null);
        setBusiness(null);
        setCustomer(null);
        setLoading(false);
        return;
      }

      const inv = { id: invoiceSnap.id, ...invoiceSnap.data() };
      setInvoice(inv);

      const [bizSnap, custSnap] = await Promise.all([
        getDoc(doc(db, 'businesses', inv.business_id)),
        getDoc(doc(db, 'customers', inv.customer_id)),
      ]);

      setBusiness(bizSnap.exists() ? { id: bizSnap.id, ...bizSnap.data() } : null);
      setCustomer(custSnap.exists() ? { id: custSnap.id, ...custSnap.data() } : null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch invoice data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsExporting(true);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      
      const response = await axios.post(`${API_URL}/api/pdf`, {
        invoice,
        business,
        customer
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number || 'draft'}.pdf`);
      document.body.appendChild(link);
      
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice PDF downloaded successfully');
    } catch (error) {
      console.error('PDF Generation Error:', error);
      let errorMessage = error.message;
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsedJSON = JSON.parse(text);
          errorMessage = parsedJSON.error || parsedJSON.message || errorMessage;
        } catch (_) {
          // not JSON or parsing failed, fallback to generic message
        }
      }
      toast.error(`Failed to generate PDF: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Not logged in');
        return;
      }

      await addDoc(collection(db, 'recurringSchedules'), {
        invoiceId,
        frequency: recurringData.frequency,
        start_date: recurringData.start_date,
        end_date: recurringData.end_date || null,
        userId: user.uid,
        is_active: true,
        next_generation: recurringData.start_date,
        createdAt: serverTimestamp(),
      });

      toast.success('Recurring schedule created successfully');
      setRecurringDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to create recurring schedule');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const invoiceDocRef = doc(db, 'invoices', invoiceId);
      await updateDoc(invoiceDocRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      toast.success('Status updated successfully');
      await fetchInvoiceData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!invoice || !business || !customer) {
    return (
      <div className="p-8 md:p-12">
        <p className="text-muted-foreground">Invoice not found</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      unpaid: 'bg-amber-100 text-amber-700 border-amber-200',
      overdue: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-200 text-gray-600 border-gray-300',
      proforma: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-semibold uppercase tracking-wide border ${
          styles[status] || ''
        }`}
      >
        {status}
      </span>
    );
  };

  const calcSubtotal = invoice.items.reduce((sum, item) => {
    const q = Number(item.quantity ?? 0);
    const r = Number(item.rate ?? 0);
    return sum + q * r;
  }, 0);

  const calcDiscount = invoice.items.reduce((sum, item) => {
    const itemTotal = (Number(item.quantity ?? 0) * Number(item.rate ?? 0));
    let discountAmt = 0;
    if (item.discount_type === 'percentage') {
      discountAmt = itemTotal * (Number(item.discount_value ?? 0) / 100);
    } else if (item.discount_type === 'fixed') {
      discountAmt = Number(item.discount_value ?? 0);
    }
    return sum + (discountAmt || 0);
  }, 0);

  const calcTotal = calcSubtotal - calcDiscount;
  const showDiscountColumn = calcDiscount > 0;

  const renderStandardLayout = () => {
    return (
      <>
        {/* Header */}
        <div className="flex items-start justify-between mb-8 pb-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-1">
              INVOICE
            </h1>
            <p className="text-sm font-medium text-slate-500">#{invoice.invoice_number}</p>
          </div>
          <div className="text-right">
            {business.logo && (
              <img
                src={business.logo}
                alt={business.name}
                className="h-14 max-w-[180px] object-contain inline-block"
                crossOrigin="anonymous"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {/* Mark as Paid / Unpaid – visible only in UI, hidden in PDF */}
            <div className="mt-4 space-x-2 invoice-action-buttons">
              {invoice.status !== 'paid' && (
                <Button
                  size="sm"
                  onClick={() => handleStatusChange('paid')}
                  data-testid="mark-paid-button"
                >
                  Mark as Paid
                </Button>
              )}
              {invoice.status !== 'unpaid' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStatusChange('unpaid')}
                  data-testid="mark-unpaid-button"
                >
                  Mark as Unpaid
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-2 gap-4 border-y border-slate-100 py-6 mb-8 text-sm">
          <div>
            <p className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mb-1">Date Issued</p>
            <p className="font-semibold text-slate-800">{invoice.invoice_date}</p>
          </div>
          <div>
            <p className="text-slate-400 font-semibold uppercase tracking-wider text-[10px] mb-1">Due Date</p>
            <p className="font-semibold text-slate-800">{invoice.due_date}</p>
          </div>
        </div>

        {/* Business & Customer Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 text-sm">
          <div className="bg-slate-50/50 p-6 rounded-xl border border-slate-100/80">
            <h3 className="font-bold text-[10px] uppercase tracking-wider text-slate-400 mb-3">Issued By</h3>
            <h4 className="font-bold text-base text-slate-800 mb-1">{business.name}</h4>
            <div className="space-y-1 text-slate-600">
              {business.address && <p>{business.address}</p>}
              {business.email && <p>{business.email}</p>}
              {business.phone && <p>{business.phone}</p>}
              {business.gst_number && invoice.template_type !== 'non-gst' && (
                <p className="text-xs font-semibold text-slate-500 mt-2">
                  GST: {business.gst_number}
                </p>
              )}
            </div>
          </div>
          <div className="bg-slate-50/50 p-6 rounded-xl border border-slate-100/80">
            <h3 className="font-bold text-[10px] uppercase tracking-wider text-slate-400 mb-3">Prepared For</h3>
            <h4 className="font-bold text-base text-slate-800 mb-1">{customer.name}</h4>
            <div className="space-y-1 text-slate-600">
              {customer.company && <p>{customer.company}</p>}
              {customer.address && <p>{customer.address}</p>}
              {customer.email && <p>{customer.email}</p>}
              {customer.phone && <p>{customer.phone}</p>}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-8 overflow-x-auto">
          <div className="min-w-[800px]">
            <div
              className={`bg-slate-50 border-y border-slate-200 p-4 grid gap-4 font-semibold text-[10px] text-slate-500 uppercase tracking-wider ${
                showDiscountColumn ? 'grid-cols-6' : 'grid-cols-5'
              }`}
            >
              <div className="col-span-2">Description</div>
              <div className="text-right">Quantity</div>
              <div className="text-right">Rate</div>
              {showDiscountColumn && <div className="text-right">Discount</div>}
              <div className="text-right">Total</div>
            </div>
            {invoice.items.map((item, index) => {
              const itemTotal = Number(item.quantity ?? 0) * Number(item.rate ?? 0);
              let discountAmount = 0;
              if (item.discount_type === 'percentage') {
                discountAmount = itemTotal * (Number(item.discount_value ?? 0) / 100);
              } else if (item.discount_type === 'fixed') {
                discountAmount = Number(item.discount_value ?? 0);
              }
              const afterDiscount = itemTotal - discountAmount;
              const tax = afterDiscount * (Number(item.tax_percentage ?? 0) / 100);
              const totalItem = afterDiscount + tax;

              return (
                <div
                  key={index}
                  className={`p-4 border-b border-slate-100 grid gap-4 text-sm text-slate-700 items-center ${
                    showDiscountColumn ? 'grid-cols-6' : 'grid-cols-5'
                  }`}
                  data-testid={`invoice-item-${index}`}
                >
                  <div className="col-span-2">
                    <p className="font-semibold text-slate-800">{item.description}</p>
                    {item.tax_percentage > 0 && invoice.template_type !== 'non-gst' && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Tax: {item.tax_percentage}%
                      </p>
                    )}
                  </div>
                  <div
                    className="text-right"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {item.quantity}
                  </div>
                  <div
                    className="text-right"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {Number(item.rate).toFixed(2)}
                  </div>
                  {showDiscountColumn && (
                    <div
                      className="text-right"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {discountAmount.toFixed(2)}
                    </div>
                  )}
                  <div
                    className="text-right font-semibold text-slate-800"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {totalItem.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-full max-w-md bg-slate-50/50 p-6 rounded-xl border border-slate-100/80 space-y-3 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal:</span>
              <span
                className="font-semibold text-slate-800"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {calcSubtotal.toFixed(2)}
              </span>
            </div>
            {calcDiscount > 0 && (
              <div className="flex justify-between text-emerald-600 font-medium">
                <span>Discount:</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  -{calcDiscount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-slate-900 pt-3 border-t border-slate-200">
              <span>Total:</span>
              <span
                className="text-primary text-lg"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {customer.currency} {calcTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="pt-6 border-t border-slate-100 mt-8 text-xs text-slate-400">
            <h3 className="font-bold uppercase tracking-wider text-[10px] text-slate-400 mb-2">
              Notes & Terms
            </h3>
            <p className="leading-relaxed">{invoice.notes}</p>
          </div>
        )}
      </>
    );
  };

  const renderInvoiceLayout = () => {
    // Calculations
    const afterItemDiscount = calcSubtotal - calcDiscount;

    let globalDiscountAmt = 0;
    if (invoice.global_discount_type === 'percentage') {
      globalDiscountAmt = afterItemDiscount * (Number(invoice.global_discount_value ?? 0) / 100);
    } else if (invoice.global_discount_type === 'fixed') {
      globalDiscountAmt = Number(invoice.global_discount_value ?? 0);
    }

    const afterGlobalDiscount = afterItemDiscount - globalDiscountAmt;

    // Calculate taxes per item and total taxes
    let cgstTotal = 0;
    let sgstTotal = 0;

    // Group tax details by HSN/SAC
    const taxSummaryMap = {};

    invoice.items.forEach(item => {
      const itemSubtotal = Number(item.quantity ?? 0) * Number(item.rate ?? 0);
      let itemDiscount = 0;
      if (item.discount_type === 'percentage') {
        itemDiscount = itemSubtotal * (Number(item.discount_value ?? 0) / 100);
      } else if (item.discount_type === 'fixed') {
        itemDiscount = Number(item.discount_value ?? 0);
      }
      const itemTaxable = itemSubtotal - itemDiscount;

      const taxRate = Number(item.tax_percentage ?? 0);
      const taxAmount = itemTaxable * (taxRate / 100);
      const cgstAmt = taxAmount / 2;
      const sgstAmt = taxAmount / 2;

      cgstTotal += cgstAmt;
      sgstTotal += sgstAmt;

      const hsn = item.hsn_sac || 'N/A';
      if (!taxSummaryMap[hsn]) {
        taxSummaryMap[hsn] = {
          hsn_sac: hsn,
          taxableAmount: 0,
          cgstRate: taxRate / 2,
          cgstAmt: 0,
          sgstRate: taxRate / 2,
          sgstAmt: 0,
          totalTax: 0
        };
      }
      taxSummaryMap[hsn].taxableAmount += itemTaxable;
      taxSummaryMap[hsn].cgstAmt += cgstAmt;
      taxSummaryMap[hsn].sgstAmt += sgstAmt;
      taxSummaryMap[hsn].totalTax += taxAmount;
    });

    const taxSummaryList = Object.values(taxSummaryMap);

    const totalTaxAmt = cgstTotal + sgstTotal;
    const rawTotal = afterGlobalDiscount + totalTaxAmt;
    const roundedTotal = Math.round(rawTotal);
    const roundOff = roundedTotal - rawTotal;

    const currencySymbol = customer.currency === 'INR' ? '₹' : customer.currency;

    // Terms
    const defaultTerms = [
      'Total Payment Due on Mentioned as due date',
      'Please include the Invoice No. on your Cheque',
      'Goods once sold/services rendered will not be refunded.',
      'All disputes shall be subject to local jurisdiction.',
      'Interest Clause: A late payment interest of 18% per annum will be charged beyond the credit period'
    ];
    const termsList = invoice.terms_conditions
      ? invoice.terms_conditions.split('\n').filter(t => t.trim().length > 0)
      : defaultTerms;

    return (
      <div className="w-full text-slate-800 font-sans p-0.5 select-text text-left" style={{ fontSize: '11px', lineHeight: '1.35', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        
        {/* Top Header */}
        <div className="relative border-b border-slate-800 pb-1 mb-2.5 text-center flex justify-center items-center">
          <div className="text-xl font-extrabold tracking-wider text-slate-800">
            {invoice.status === 'proforma' ? 'PROFORMA INVOICE' : (invoice.template_type === 'non-gst' ? 'INVOICE' : 'TAX INVOICE')}
          </div>
          <div className="absolute right-0 bottom-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Original For Recipient
          </div>
        </div>

        {/* Business Box */}
        <div className="border border-slate-400 p-2.5 mb-2.5 rounded flex items-center justify-between">
          <div className="flex items-center gap-4">
            {business.logo ? (
              <img
                id="invoice-logo"
                src={business.logo}
                alt={business.name}
                style={{ height: '48px', width: 'auto', maxWidth: '180px', objectFit: 'contain' }}
                crossOrigin="anonymous"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-16 h-16 bg-slate-100 flex items-center justify-center font-bold text-lg text-slate-400 border rounded">
                LOGO
              </div>
            )}
            <div>
              <h2 className="text-base font-bold text-slate-900 leading-tight">{business.name}</h2>
              <p className="text-[11px] text-slate-600 max-w-[500px] mt-0.5">{business.address}</p>
              <div className="text-[10px] text-slate-500 mt-1 space-x-2">
                {business.phone && <span>Phone: <strong className="text-slate-700">{business.phone}</strong></span>}
                {business.email && <span>Email: <strong className="text-slate-700">{business.email}</strong></span>}
              </div>
            </div>
          </div>
          <div className="text-right text-[11px]">
            {business.gst_number && invoice.template_type !== 'non-gst' && (
              <div>GSTIN: <strong className="text-slate-800">{business.gst_number}</strong></div>
            )}
            {invoice.template_type !== 'non-gst' && (
              <div>State: <strong className="text-slate-800">{invoice.business_state || business.state || 'N/A'}</strong></div>
            )}
          </div>
        </div>

        {/* Details Grid: Bill To / Invoice Details */}
        <div className="grid grid-cols-2 border border-slate-400 rounded overflow-hidden mb-2.5">
          {/* Bill To */}
          <div className="border-r border-slate-400 p-3">
            <h3 className="bg-slate-100 font-bold uppercase tracking-wider text-[10px] px-2 py-1 mb-2 border-b border-slate-300 -mx-3 -mt-3">
              Bill To:
            </h3>
            <div className="font-bold text-slate-900">{customer.company || customer.name}</div>
            {customer.company && <div className="text-[11px] text-slate-600">{customer.name}</div>}
            <div className="text-[11px] text-slate-600 mt-1 max-w-[320px]">{customer.address}</div>
            <div className="text-[11px] text-slate-700 mt-2 space-y-0.5">
              {invoice.template_type !== 'non-gst' && (
                <>
                  <div>GSTIN: <strong>{invoice.customer_gstin || customer.gst_number || 'N/A'}</strong></div>
                  <div>State: <strong>{invoice.customer_state || customer.state || 'N/A'}</strong></div>
                </>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-3">
            <h3 className="bg-slate-100 font-bold uppercase tracking-wider text-[10px] px-2 py-1 mb-2 border-b border-slate-300 -mx-3 -mt-3">
              Invoice Details:
            </h3>
            <table className="w-full text-[11px]">
              <tbody>
                <tr>
                  <td className="text-slate-500 py-0.5">No:</td>
                  <td className="font-bold text-slate-800 py-0.5 text-right">{invoice.invoice_number}</td>
                </tr>
                <tr>
                  <td className="text-slate-500 py-0.5">Date:</td>
                  <td className="font-bold text-slate-800 py-0.5 text-right">{formatDate(invoice.invoice_date)}</td>
                </tr>
                <tr>
                  <td className="text-slate-500 py-0.5">Due Date:</td>
                  <td className="font-bold text-slate-800 py-0.5 text-right">{formatDate(invoice.due_date)}</td>
                </tr>
                <tr>
                  <td className="text-slate-500 py-0.5">Place Of Supply:</td>
                  <td className="font-bold text-slate-800 py-0.5 text-right">{invoice.place_of_supply || invoice.customer_state || customer.state || 'N/A'}</td>
                </tr>
                {invoice.po_no && (
                  <tr>
                    <td className="text-slate-500 py-0.5">PO No:</td>
                    <td className="font-bold text-slate-800 py-0.5 text-right">{invoice.po_no}</td>
                  </tr>
                )}
                {invoice.po_date && (
                  <tr>
                    <td className="text-slate-500 py-0.5">PO Date:</td>
                    <td className="font-bold text-slate-800 py-0.5 text-right">{formatDate(invoice.po_date)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Table */}
        <div className="border border-slate-400 rounded overflow-hidden mb-2.5">
          <table className="w-full text-[11px] border-collapse" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {showDiscountColumn ? (
                <>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: invoice.template_type === 'non-gst' ? '53%' : '43%' }} />
                  {invoice.template_type !== 'non-gst' && <col style={{ width: '10%' }} />}
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                </>
              ) : (
                <>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: invoice.template_type === 'non-gst' ? '63%' : '51%' }} />
                  {invoice.template_type !== 'non-gst' && <col style={{ width: '12%' }} />}
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '11%' }} />
                </>
              )}
            </colgroup>
            <thead>
              <tr className="bg-slate-100 border-b border-slate-400 font-bold text-slate-700">
                <th className="border-r border-slate-400 py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>#</th>
                <th className="border-r border-slate-400 py-1.5 px-2 text-left align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Item name</th>
                {invoice.template_type !== 'non-gst' && (
                  <th className="border-r border-slate-400 py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>HSN/SAC</th>
                )}
                <th className="border-r border-slate-400 py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Quantity</th>
                <th className="border-r border-slate-400 py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Price/Unit</th>
                {showDiscountColumn && (
                  <th className="border-r border-slate-400 py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Discount</th>
                )}
                <th className="py-1.5 px-2 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => {
                const itemSubtotal = Number(item.quantity ?? 0) * Number(item.rate ?? 0);
                let discountAmount = 0;
                if (item.discount_type === 'percentage') {
                  discountAmount = itemSubtotal * (Number(item.discount_value ?? 0) / 100);
                } else if (item.discount_type === 'fixed') {
                  discountAmount = Number(item.discount_value ?? 0);
                }
                const itemTaxable = itemSubtotal - discountAmount;

                return (
                  <tr key={index} className="border-b border-slate-300 hover:bg-slate-50/50">
                    <td className="border-r border-slate-400 py-2 px-2 text-center align-middle" style={{ verticalAlign: 'middle' }}>{index + 1}</td>
                    <td className="border-r border-slate-400 py-2 px-2 text-left break-words align-middle" style={{ verticalAlign: 'middle' }}>{item.description}</td>
                    {invoice.template_type !== 'non-gst' && (
                      <td className="border-r border-slate-400 py-2 px-2 text-center whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{item.hsn_sac || '-'}</td>
                    )}
                    <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{Number(item.quantity).toFixed(2)}</td>
                    <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{Number(item.rate).toFixed(2)}</td>
                    {showDiscountColumn && (
                      <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>
                        {discountAmount > 0 ? discountAmount.toFixed(2) : '-'}
                      </td>
                    )}
                    <td className="py-2 px-2 text-center font-mono font-semibold whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{itemTaxable.toFixed(2)}</td>
                  </tr>
                );
              })}
              {/* Table Total Row */}
              <tr className="bg-slate-50 border-t border-slate-400 font-bold">
                <td className="border-r border-slate-400 py-2 px-2 text-center align-middle" style={{ verticalAlign: 'middle' }}></td>
                <td className="border-r border-slate-400 py-2 px-2 text-left align-middle" style={{ verticalAlign: 'middle' }}>Total</td>
                {invoice.template_type !== 'non-gst' && (
                  <td className="border-r border-slate-400 py-2 px-2 text-center align-middle" style={{ verticalAlign: 'middle' }}></td>
                )}
                <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>
                  {invoice.items.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0).toFixed(2)}
                </td>
                <td className="border-r border-slate-400 py-2 px-2 text-center align-middle" style={{ verticalAlign: 'middle' }}></td>
                {showDiscountColumn && (
                  <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>
                    {calcDiscount.toFixed(2)}
                  </td>
                )}
                <td className="py-2 px-2 text-center font-mono text-slate-900 whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{(calcSubtotal - calcDiscount).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Tax Summary Table */}
        {invoice.template_type !== 'non-gst' && (
        <div className="border border-slate-400 rounded overflow-hidden mb-2.5">
          <table className="w-full text-[10px] border-collapse text-center" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr className="bg-slate-100 border-b border-slate-400 font-bold text-slate-700">
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>HSN/SAC</th>
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Taxable Amt</th>
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>CGST (%)</th>
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>CGST Amt</th>
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>SGST (%)</th>
                <th className="border-r border-slate-400 py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>SGST Amt</th>
                <th className="py-1.5 px-1 text-center align-middle whitespace-nowrap" style={{ verticalAlign: 'middle' }}>Total Tax</th>
              </tr>
            </thead>
            <tbody>
              {taxSummaryList.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-300">
                  <td className="border-r border-slate-400 py-2 px-2 font-semibold text-center whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.hsn_sac}</td>
                  <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.taxableAmount.toFixed(2)}</td>
                  <td className="border-r border-slate-400 py-2 px-1 font-mono text-center whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.cgstRate}%</td>
                  <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.cgstAmt.toFixed(2)}</td>
                  <td className="border-r border-slate-400 py-2 px-1 font-mono text-center whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.sgstRate}%</td>
                  <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.sgstAmt.toFixed(2)}</td>
                  <td className="py-2 px-2 text-center font-mono font-semibold whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{row.totalTax.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-bold border-t border-slate-400">
                <td className="border-r border-slate-400 py-2 px-2 text-center align-middle" style={{ verticalAlign: 'middle' }}>TOTAL</td>
                <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>
                  {taxSummaryList.reduce((sum, r) => sum + r.taxableAmount, 0).toFixed(2)}
                </td>
                <td className="border-r border-slate-400 py-2 px-1 text-center align-middle" style={{ verticalAlign: 'middle' }}></td>
                <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{cgstTotal.toFixed(2)}</td>
                <td className="border-r border-slate-400 py-2 px-1 text-center align-middle" style={{ verticalAlign: 'middle' }}></td>
                <td className="border-r border-slate-400 py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{sgstTotal.toFixed(2)}</td>
                <td className="py-2 px-2 text-center font-mono whitespace-nowrap align-middle" style={{ verticalAlign: 'middle' }}>{totalTaxAmt.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        )}

        {/* Totals & Bank Details Row */}
        <div className="grid grid-cols-2 gap-3 mb-2.5 text-left">
          {/* Payment & Bank Details */}
          <div className="space-y-2.5">
            <div className="border border-slate-400 rounded p-2">
              <h4 className="font-bold text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">Payment Details</h4>
              <table className="w-full text-[11px]">
                <tbody>
                  <tr>
                    <td className="text-slate-500 py-0.5 w-[120px]">Payment Mode:</td>
                    <td className="font-bold text-slate-800 py-0.5">{invoice.payment_mode || 'Credit'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="border border-slate-400 rounded p-2">
              <h4 className="font-bold text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">Bank Details</h4>
              <table className="w-full text-[11px] space-y-0.5">
                <tbody>
                  <tr>
                    <td className="text-slate-500 py-0.5 w-[120px]">Bank Name:</td>
                    <td className="font-bold text-slate-800 py-0.5">{invoice.bank_name || business.bank_name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-slate-500 py-0.5">Account No:</td>
                    <td className="font-bold text-slate-800 py-0.5">{invoice.bank_account_no || business.bank_account_no || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-slate-500 py-0.5">IFSC Code:</td>
                    <td className="font-bold text-slate-800 py-0.5">{invoice.bank_ifsc || business.bank_ifsc || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-slate-500 py-0.5">Account Holder:</td>
                    <td className="font-bold text-slate-800 py-0.5">{invoice.bank_account_holder || business.bank_account_holder || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Summary */}
          <div className="border border-slate-400 rounded p-2.5 bg-slate-50/50 flex flex-col justify-between">
            <div className="space-y-2 text-[11px]">
              <div className="flex justify-between text-slate-600">
                <span>Sub Total ({currencySymbol}):</span>
                <span className="font-semibold font-mono">{calcSubtotal.toFixed(2)}</span>
              </div>
              {calcDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Item Discounts ({currencySymbol}):</span>
                  <span className="font-semibold font-mono">-{calcDiscount.toFixed(2)}</span>
                </div>
              )}
              {globalDiscountAmt > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Global Discount ({currencySymbol}):</span>
                  <span className="font-semibold font-mono">-{globalDiscountAmt.toFixed(2)}</span>
                </div>
              )}
              {cgstTotal > 0 && invoice.template_type !== 'non-gst' && (
                <div className="flex justify-between text-slate-600">
                  <span>CGST ({currencySymbol}):</span>
                  <span className="font-semibold font-mono">{cgstTotal.toFixed(2)}</span>
                </div>
              )}
              {sgstTotal > 0 && invoice.template_type !== 'non-gst' && (
                <div className="flex justify-between text-slate-600">
                  <span>SGST ({currencySymbol}):</span>
                  <span className="font-semibold font-mono">{sgstTotal.toFixed(2)}</span>
                </div>
              )}
              {roundOff !== 0 && (
                <div className="flex justify-between text-slate-500">
                  <span>Round Off ({currencySymbol}):</span>
                  <span className="font-semibold font-mono">
                    {roundOff > 0 ? '+' : ''}{roundOff.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm font-extrabold text-slate-900 border-t border-slate-300 pt-2 text-left">
                <span>Grand Total ({currencySymbol}):</span>
                <span className="font-mono">{roundedTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Total in Words */}
            <div className="mt-3 pt-2 border-t border-slate-200">
              <div className="text-[10px] uppercase font-bold text-slate-400 mb-1 text-left">Invoice Amount in Words:</div>
              <div className="text-[11px] font-bold text-slate-800 italic text-left">
                {numberToWords(roundedTotal, customer.currency)}
              </div>
            </div>
          </div>
        </div>

        {/* Terms & Conditions & Signatory Row */}
        <div className="grid grid-cols-2 gap-3 border-t border-slate-300 pt-2.5 text-left">
          {/* Terms */}
          <div>
            <h4 className="font-bold text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">Terms & Conditions</h4>
            <ol className="list-decimal pl-4 text-[10px] text-slate-600 space-y-0.5">
              {termsList.map((term, i) => (
                <li key={i}>{term}</li>
              ))}
            </ol>
          </div>

          {/* Signature */}
          <div className="flex flex-col justify-between items-end h-full min-h-[80px]">
            <div className="text-[11px] font-bold text-slate-700">
              For {business.name}:
            </div>
            <div className="text-center mt-3">
              <div className="w-[180px] border-b border-dashed border-slate-500 mb-1"></div>
              <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">
                Authorized Signatory
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="invoice-detail-page">
      <div className="max-w-5xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="gap-2"
            data-testid="back-to-invoices"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Invoices
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="gap-2"
              data-testid="download-pdf-button"
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download PDF
                </>
              )}
            </Button>
            <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="setup-recurring-button">
                  <RefreshCw className="w-4 h-4" />
                  Setup Recurring
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Setup Recurring Invoice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateRecurring} className="space-y-4 mt-4">
                  <div>
                    <Label>Frequency</Label>
                    <select
                      className="w-full px-3 py-2 border border-input rounded-md bg-white"
                      value={recurringData.frequency}
                      onChange={(e) =>
                        setRecurringData({ ...recurringData, frequency: e.target.value })
                      }
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={recurringData.start_date}
                      onChange={(e) =>
                        setRecurringData({ ...recurringData, start_date: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={recurringData.end_date}
                      onChange={(e) =>
                        setRecurringData({ ...recurringData, end_date: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setRecurringDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-recurring-submit">
                      Create Schedule
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Everything inside goes into the PDF */}
        <div className="w-full overflow-x-auto pb-4">
          <div
            ref={invoiceRef}
            style={{
              width: '210mm',
              minWidth: '210mm',
              minHeight: '297mm',
              boxSizing: 'border-box',
              padding: '10mm',
              margin: '0 auto',
              backgroundColor: '#ffffff',
            }}
            className="relative shadow-invoice border border-border/50 overflow-hidden rounded-xl text-card-foreground text-left"
          >
            {renderInvoiceLayout()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
